/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Modal, Input, Button, message } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import { queryCache, useMutation } from 'react-query';
import {
  addCustomMovement,
  addCustomToMovement,
} from '~src/services/api/movements';
import { MovementEntry } from '~components/atoms/FileTables/TenderMovements/TenderMovementsTable';
import { useIntl } from 'react-intl';
import { appLog } from '~src/utils/log';

interface AddCustomToMovementParams extends ModalProps {
  projectId: number | undefined;
  tenderId: number | string;
  movement?: MovementEntry;
}

const initialFormsValues = {
  note: '',
  new_value: '',
};

export const AddCustomToMovement: React.FC<AddCustomToMovementParams> = ({
  projectId,
  tenderId,
  movement,
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();
  const [formValues, setFormValues] = useState(initialFormsValues);

  const [doAddCustomToMovement, { isLoading }] = useMutation(
    addCustomToMovement,
    {
      onSuccess: () => {
        queryCache.invalidateQueries('movements');
        message.success(intl.formatMessage({ id: 'success' }));
      },
      onError: () => {
        message.error('Failed');
      },
    }
  );

  const isValid = () => {
    if (!formValues.note) return false;
    if (!formValues.new_value) return false;

    return true;
  };

  if (!projectId && !tenderId && !movement?.id) return null;

  const handleSubmit = async () => {
    appLog(`submited:`, formValues);

    const movementId =
      movement?.originalId || Number(String(movement?.id).split('_')[0]);

    await doAddCustomToMovement({
      project_id: projectId,
      tender_id: Number(tenderId),
      movement_id: movementId,
      body: {
        note: formValues.note,
        field: movement?.changes[0]?.field || '',
        old_value: movement?.changes[0]?.new_value || '',
        new_value: formValues.new_value,
      },
    });

    setFormValues(initialFormsValues);
    onCancel && onCancel(undefined as any);
  };

  appLog(`movement`, movement);

  return (
    <Modal
      title={
        <div css={{ textAlign: 'center' }}>
          {intl.formatMessage({ id: 'add-custom-to-movement' })}
          <br />
          <span css={{ color: '#888' }}>{movement?.summary?.head}</span>
        </div>
      }
      width="min(90vw, 400px)"
      bodyStyle={{ padding: '20px' }}
      centered
      destroyOnClose
      maskClosable={true}
      onCancel={onCancel}
      footer={null}
      {...modalProps}>
      <div
        style={{
          padding: '10px',
          width: '100%',
          // backgroundColor: 'red',
          display: 'flex',
          justifyContent: 'center',
        }}>
        <div
          css={{
            width: '90%',
            // backgroundColor: 'blue'
            gap: '10px',
            display: 'flex',
            flexFlow: 'column nowrap',
          }}>
          <div>
            <label>{intl.formatMessage({ id: 'note' })}</label>
            <Input
              placeholder={intl.formatMessage({ id: 'note' })}
              value={formValues.note}
              onChange={(e) => {
                setFormValues({ ...formValues, note: e.target.value });
              }}
              allowClear
            />
          </div>
          <div>
            <label>{intl.formatMessage({ id: 'field' })}</label>
            <Input
              placeholder={intl.formatMessage({ id: 'field' })}
              value={movement?.changes[0]?.field}
              disabled={true}
              allowClear
            />
          </div>
          <div>
            <label>{intl.formatMessage({ id: 'old-value' })}</label>
            <Input
              placeholder={intl.formatMessage({ id: 'old-value' })}
              value={movement?.changes[0]?.new_value}
              disabled={true}
              allowClear
            />
          </div>
          <div>
            <label>{intl.formatMessage({ id: 'new-value' })}</label>
            <Input
              placeholder={intl.formatMessage({ id: 'new-value' })}
              value={formValues.new_value}
              onChange={(e) => {
                setFormValues({ ...formValues, new_value: e.target.value });
              }}
              allowClear
            />
          </div>
          <div
            css={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              marginTop: '25px',
              gap: '10px',
            }}>
            <Button type="default" onClick={onCancel} disabled={isLoading}>
              {intl.formatMessage({ id: 'close' })}
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={!isValid()}
              loading={isLoading}>
              {intl.formatMessage({ id: 'save' })}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
